import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useShoppingList, usePantry } from '../hooks/useShopping';
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import clsx from 'clsx';
import { ProtectedRoute } from '../components/auth';

export const Route = createFileRoute('/shopping')({
  component: ProtectedShoppingPage,
});

function ProtectedShoppingPage() {
  return (
    <ProtectedRoute>
      <ShoppingPage />
    </ProtectedRoute>
  );
}

const CATEGORY_CONFIG: Record<string, { emoji: string; label: string }> = {
  produce: { emoji: '🥬', label: 'Produce' },
  dairy: { emoji: '🧀', label: 'Dairy & Eggs' },
  meat: { emoji: '🥩', label: 'Meat & Poultry' },
  seafood: { emoji: '🐟', label: 'Seafood' },
  bakery: { emoji: '🥖', label: 'Bakery' },
  frozen: { emoji: '❄️', label: 'Frozen' },
  pantry: { emoji: '🥡', label: 'Pantry' },
  beverages: { emoji: '🍷', label: 'Beverages' },
  other: { emoji: '📦', label: 'Other' },
};

function ShoppingPage() {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const [_showConfetti, setShowConfetti] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');
  const [newItemUnit, setNewItemUnit] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('other');
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/' });
    }
  }, [isLoaded, isSignedIn, navigate]);
  
  const { 
    list: shoppingList, 
    toggleItem: toggleItemConvex,
    addItem: addItemConvex,
    removeItem: _removeItemConvex,
    clearCompleted: clearCompletedConvex,
    moveToPantry: moveToPantryConvex,
    isLoading: isListLoading,
    refetch
  } = useShoppingList();

  const {
    items: _pantryItems,
    addToPantry: _addToPantryConvex,
    isLoading: _isPantryLoading,
  } = usePantry();
  
  const toggleItem = useMutation({
    mutationFn: async ({ itemId, checked }: { itemId: string; checked: boolean }) => {
      return toggleItemConvex({ itemId, checked });
    },
  });
  
  const addItem = useMutation({
    mutationFn: async () => {
      const amount = newItemAmount ? parseFloat(newItemAmount) : undefined;
      return addItemConvex({
        ingredientName: newItemName,
        amount,
        unit: newItemUnit || undefined,
        category: newItemCategory,
        addedManually: true,
      });
    },
    onSuccess: () => {
      setNewItemName('');
      setNewItemAmount('');
      setNewItemUnit('');
      setNewItemCategory('other');
      setShowAddForm(false);
      refetch();
    },
    onError: (error) => {
      alert(error instanceof Error ? error.message : 'Failed to add item');
    },
  });
  
  // Clear completed items
  const clearCompleted = useMutation({
    mutationFn: async () => {
      return clearCompletedConvex();
    },
    onSuccess: () => {
      refetch();
    },
  });

  // Move item to pantry
  const moveToPantry = useMutation({
    mutationFn: async (itemId: string) => {
      return moveToPantryConvex(itemId);
    },
    onSuccess: () => {
      refetch();
    },
  });
  
  const exportText = useMutation({
    mutationFn: async () => {
      if (!shoppingList?.items) return '';
      const items = shoppingList.items.filter((i: any) => !i.checked);
      return items
        .map((item: any) => {
          const parts = [item.ingredientName];
          if (item.amount) parts.unshift(String(item.amount));
          if (item.unit) parts.splice(1, 0, item.unit);
          return `- ${parts.join(' ')}`;
        })
        .join('\n');
    },
    onSuccess: (text: string) => {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
  });
  
  // Sort items: unchecked first, then by category, then by name
  const sortedItems = useMemo(() => {
    if (!shoppingList?.items) return [];
    
    return [...shoppingList.items].sort((a: any, b: any) => {
      // Checked items at the bottom
      if (a.checked !== b.checked) {
        return a.checked ? 1 : -1;
      }
      // Then by category
      if (a.category !== b.category) {
        const catOrder = Object.keys(CATEGORY_CONFIG);
        const aIndex = catOrder.indexOf(a.category);
        const bIndex = catOrder.indexOf(b.category);
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        return a.category.localeCompare(b.category);
      }
      // Then by name
      return a.ingredientName.localeCompare(b.ingredientName);
    });
  }, [shoppingList?.items]);

  const totalCount = shoppingList?.items?.length || 0;
  const uncheckedCount = shoppingList?.items?.filter((i: any) => !i.checked).length || 0;
  const checkedCount = totalCount - uncheckedCount;
  const progress = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;

  useEffect(() => {
    if (progress === 100 && totalCount > 0) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [progress, totalCount]);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-stone">Loading...</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return null;
  }

  if (isListLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-stone">Loading shopping list...</div>
        </div>
      </div>
    );
  }

  const hasItems = sortedItems.length > 0;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="section-title">Shopping List</h1>
        <p className="text-subtitle">
          {uncheckedCount} items to buy
          {checkedCount > 0 && ` · ${checkedCount} completed`}
        </p>
      </div>
      
      {hasItems ? (
        <>
          {/* Progress bar */}
          <div className="card p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-2xl font-display font-semibold text-ink">
                  {checkedCount}
                </span>
                <span className="text-stone text-lg"> / {totalCount}</span>
                <span className="text-stone ml-2">items</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => exportText.mutate()}
                  className="btn btn-secondary btn-sm"
                >
                  {copied ? '✅ Copied!' : '📋 Copy'}
                </button>
              </div>
            </div>
            
            <div className="h-3 bg-sand rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full transition-all duration-500 ease-out',
                  progress === 100 
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                    : 'bg-gradient-to-r from-coral-400 to-coral-500'
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Clear completed button */}
          {checkedCount > 0 && (
            <div className="mb-4">
              <button
                onClick={() => {
                  if (confirm(`Clear ${checkedCount} completed items?`)) {
                    clearCompleted.mutate();
                  }
                }}
                disabled={clearCompleted.isPending}
                className="btn btn-ghost text-stone hover:text-rose-600 hover:bg-rose-50 text-sm"
              >
                {clearCompleted.isPending ? 'Clearing...' : `✓ Clear ${checkedCount} completed`}
              </button>
            </div>
          )}

          {/* Unified list */}
          <div className="card divide-y divide-mist">
            {sortedItems.map((item: any) => (
              <div
                key={item.id}
                className={clsx(
                  'flex items-center gap-3 p-4 transition-colors group',
                  item.checked && 'bg-sand/30'
                )}
              >
                <button
                  onClick={() => toggleItem.mutate({ itemId: item.id, checked: !item.checked })}
                  className={clsx(
                    'w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors flex-shrink-0',
                    item.checked
                      ? 'bg-coral-500 border-coral-500 text-white'
                      : 'border-mist hover:border-coral-300'
                  )}
                >
                  {item.checked && (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
                
                <div className={clsx('flex-1 min-w-0', item.checked && 'line-through text-stone')}>
                  <div className="font-medium text-ink truncate">
                    {item.amount > 0 && `${item.amount} `}
                    {item.unit && `${item.unit} `}
                    {item.ingredientName}
                  </div>
                  <div className="text-xs text-stone">
                    {CATEGORY_CONFIG[item.category]?.emoji || '📦'} {CATEGORY_CONFIG[item.category]?.label || item.category}
                  </div>
                </div>

                {/* Have at home button - only show when checked */}
                {item.checked && (
                  <button
                    onClick={() => moveToPantry.mutate(item.id)}
                    disabled={moveToPantry.isPending}
                    className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                    title="Add to pantry so it's excluded next time"
                  >
                    Have at home
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="card p-8 sm:p-12 text-center">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-sage-100 to-sage-200 flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">🛒</span>
          </div>
          <h2 className="font-display text-2xl font-semibold text-ink mb-2">
            Your list is empty
          </h2>
          <p className="text-stone mb-6 max-w-sm mx-auto">
            Add items manually or add recipes to your meal plan to auto-generate your shopping list
          </p>
        </div>
      )}
              
      <div className="mt-6">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="w-full btn btn-secondary"
        >
          {showAddForm ? 'Cancel' : '+ Add Item'}
        </button>
      </div>
      
      {showAddForm && (
        <div className="card p-4 mt-4">
          <h3 className="font-display font-semibold mb-4">Add Item</h3>
          <div className="space-y-3">
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="Item name"
              className="input w-full"
            />
            <div className="flex gap-2">
              <input
                type="number"
                value={newItemAmount}
                onChange={(e) => setNewItemAmount(e.target.value)}
                placeholder="Amount"
                className="input w-24"
              />
              <input
                type="text"
                value={newItemUnit}
                onChange={(e) => setNewItemUnit(e.target.value)}
                placeholder="Unit"
                className="input w-24"
              />
              <select
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
                className="input flex-1"
              >
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.emoji} {config.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => addItem.mutate()}
              disabled={!newItemName.trim() || addItem.isPending}
              className="btn btn-primary w-full"
            >
              {addItem.isPending ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Tip about pantry */}
      <div className="mt-8 text-center">
        <p className="text-sm text-stone">
          💡 Items you mark as "Have at home" go to your{' '}
          <a href="/kitchen" className="text-coral-600 hover:underline">Pantry</a>
          {' '}and won't appear in future shopping lists
        </p>
      </div>
    </div>
  );
}
